import React, { useCallback, useMemo, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import { Queries } from "components/Documents/Report-sections-forms/Queries";
import { Routes } from "constants/routes";
import dayjs from "dayjs";
import { useDealTitleSelector } from "store";
import { CurrencyType, DateRangeValue, DocumentReportQueries, ReportFilters } from "store/types";

import { MeasuresGroupKeys, MeasuresOption, MeasuresSelect } from "./MeasuresSelect";
import { PickParametersSet } from "./PickParametersSet";

const maxSteps = 3;

interface Props {
  onSubmit: (state: State) => void;
  onCancel: () => void;

  initialState?: State; // Edit existing inline_measure
  showGroups?: MeasuresGroupKeys[];
  excludeStaticCalcFields?: boolean;
  documentQueries?: DocumentReportQueries;
}

const titles: Record<number, string> = {
  1: "Inline measure",
  2: "Parameter set",
  3: "Measure parameter set"
};

export type State = {
  selectedOptions: MeasuresOption[];
  queries: {
    dateRange: DateRangeValue;
    currency: CurrencyType;
    filters: ReportFilters;
    exchangeMethod: "toDate" | "realDate";
  };
};

const defaultState: State = {
  selectedOptions: [],
  queries: {
    dateRange: { value: "fromInception", to: dayjs().toISOString() },
    currency: "USD",
    filters: {},
    exchangeMethod: "toDate"
  }
};

export const MeasurePrompt: React.FC<Props> = ({ onSubmit, onCancel, initialState, documentQueries }) => {
  const { pathname } = useLocation();
  const dealName = useDealTitleSelector();

  const isDealDocumentRoute = Boolean(matchPath(pathname, Routes.dealDocument)?.isExact);

  const lockedFilters: ReportFilters = useMemo(() => {
    if (isDealDocumentRoute && dealName) {
      return {
        deal_name: [dealName]
      };
    }
    return {} as ReportFilters;
  }, [isDealDocumentRoute, dealName]);

  const initializedState = useMemo<State>(() => {
    if (initialState) {
      return initialState;
    }

    return {
      ...defaultState,
      ...(isDealDocumentRoute &&
        dealName && {
          queries: {
            ...defaultState.queries,
            filters: lockedFilters
          }
        })
    };
  }, [initialState]);

  const [step, setStep] = useState<number>(1);
  const [useCurrentFilters, setUseCurrentFilters] = useState<boolean>(true);
  const [state, setState] = useState<State>(initializedState);

  const handleSetQueriesData = useCallback<(cb: (prevState: State["queries"]) => State["queries"]) => void>((cb) => {
    return setState((prev) => ({ ...prev, queries: cb(prev.queries) }));
  }, []);

  const onChangeOptions = (value: MeasuresOption[], reason?: string) => {
    if (reason === "clear") {
      setState((prev) => ({ ...prev, selectedOptions: [] }));
      return;
    }

    setState((prev) => ({ ...prev, selectedOptions: value }));
  };

  const onBack = () => {
    if (step === 1) return;

    if (step === 3 && !documentQueries) {
      return setStep(1);
    }

    if (step === 3 && initialState?.queries) {
      return setStep(1);
    }

    setStep(step - 1);
  };

  const handleSubmit = () => {
    const nextStep = step + 1;

    // if last step, submit
    if (step === maxSteps) {
      return onSubmit(state);
    }

    // Pick filters form if edit
    if (initialState?.queries) {
      return setStep(3);
    }

    if (!documentQueries) {
      setUseCurrentFilters(false);
      return setStep(3);
    }

    // Submit if we need skip filters step
    // useCurrentFilters - if true, then we use the current filters from the documentQueries
    if (nextStep === maxSteps && useCurrentFilters) {
      return onSubmit({ ...state, queries: documentQueries });
    }

    setStep(nextStep);
  };

  return (
    <Stack
      sx={{ width: 427, minHeight: 200, borderRadius: "8px", boxShadow: "sm", bgcolor: "#fff", p: "20px", gap: "20px" }}
    >
      <Stack sx={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" fontWeight="regular">
          {titles[step]}
        </Typography>
        <IconButton size="sm" onClick={onCancel}>
          <CloseIcon />
        </IconButton>
      </Stack>
      {step === 1 && <MeasuresSelect value={state.selectedOptions} onChange={onChangeOptions} />}
      {step === 2 && <PickParametersSet value={useCurrentFilters} setData={setUseCurrentFilters} />}
      {step === 3 && <Queries data={state.queries} setData={handleSetQueriesData} lockedFilters={lockedFilters} />}
      <Stack sx={{ flexDirection: "row", justifyContent: "space-between" }}>
        {step > 1 ? (
          <Button disabled={step === 1} startIcon={<ArrowBackIcon />} onClick={onBack}>
            Back
          </Button>
        ) : (
          <Box />
        )}
        <Stack sx={{ flexDirection: "row", justifyContent: "flex-end", gap: "8px" }}>
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!state.selectedOptions.length}>
            Confirm
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};
